/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';

import { Box, Typography, TextField, useMediaQuery, useTheme } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogStatus from 'layout/dialog/DialogStatus';
import { Trans, useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import AutofillDialog from 'components/autofill/AutofillDialog';
import ConfirmAutofillDialog from 'components/autofill/ConfirmAutofillDialog';
import UnitaryEffectWarningDialog from 'components/autofill/UnitaryEffectWarningDialog';
import { useSelector } from 'react-redux';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { DRAFT_VIEW } from '../useDraftManagement';
import SubmissionTable from '../../../components/submissionOverview/SimpleSubmissionTable';
import Button from '../../../components/common/Button';
import EpctCreationDialog from '../../ePCTSubmissions/EPCTCreationDialog';
import EpCreationDialog from '../../epSubmissions/EPCreationDialog';

import { SubmissionStatus } from '../../../models/SubmissionStatus';
import ImportContactDialog from '../../../components/addressBook/ImportContactDialog';
import ConfirmDelete from '../../../components/common/ConfirmDelete';
import SubmissionStatusChangeDialog from '../../../components/status/SubmissionStatusChangeDialog';
import SendSubmissionDialog from './dialogs/SendSubmissionDialog';
import SentSubmissionProgressDialog from './dialogs/SentSubmissionProgressDialog';
import { GenericDialog } from '../../../components/dialogForm/GenericDialog';
import { selectDialogIsOpen } from '../../../redux/features/dialogs/dialog.slice';
import TransferSubmissionDialog from './dialogs/TransferSubmissionDialog';
import GenericConfirmationDialog from './dialogs/GenericConfirmationDialog';

const useStyles = makeStyles(theme => ({
  ...theme.customTheme,
  tableContainer: {
    padding: '1.5rem 0 0 0',
    [theme.breakpoints.down('sm')]: {
      padding: '0.5rem 0 0 0',
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        width: '0.6rem',
        borderRadius: '0.6rem',
      },
      '&::-webkit-scrollbar:vertical': {
        width: '0.6rem',
      },
      /* Track */
      '&::-webkit-scrollbar-track': {
        background: '#f1f1f1',
        borderRadius: '0.6rem',
      },

      /* Handle */
      '&::-webkit-scrollbar-thumb': {
        background: '#888',
        borderRadius: '0.6rem',
      },
    },
  },
  gridRoot: {
    flexGrow: 1,
  },
  dFlexColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  dFlexRow: {
    display: 'flex',
    alignItems: 'center',
  },
  w60: {
    width: '60%',
  },
  mr: {
    marginRight: '0.7rem',
  },
  submissionProgressId: {
    fontWeight: '900',
  },
  deleteDialogButtons: {
    padding: '8px 32px',
  },
  scrollBar: {
    [theme.breakpoints.down('sm')]: {
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        width: '0.6rem',
        borderRadius: '0.6rem',
      },

      /* Track */
      '&::-webkit-scrollbar-track': {
        background: '#f1f1f1',
      },

      /* Handle */
      '&::-webkit-scrollbar-thumb': {
        background: '#888',
      },
    },
  },
}));

function DraftManagementDialogs({
  procedureId,
  loadData,
  view,
  selection,
  deleteDialog,
  revertStatusDialog,
  changeStatusDialog,
  fromStatus,
  toStatus,
  createEPCTDialog,
  createEPDialog,
  selectedRow,
  isAddressBookPage,
  showImportContactDialog,
  setShowImportContactDialog,
  sendSubmissionProcessDialog,
  sendSubmissionRequestProgress,
  sentSubmissions,
  sendSubmissionDialog,
  transferDialog,
  transferConfirmationDialog,
  isEPCTPage,
  isEPPage,
  isEpvalPage,
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const isMediumOrBigger = useMediaQuery(theme.breakpoints.up('md'));

  const isGenericDialogOpen = useSelector(selectDialogIsOpen);

  return (
    <>
      {isAddressBookPage ? (
        <>
          <ImportContactDialog
            loadData={loadData}
            open={showImportContactDialog}
            onCancel={() => setShowImportContactDialog(false)}
          />
          <ConfirmDelete selection={selection} deleteDialog={deleteDialog} />
        </>
      ) : (
        <>
          {/* <SubsequentActionsDialog /> */}
          {isGenericDialogOpen && <GenericDialog />}
          {isEpvalPage && <AutofillDialog procedureId={procedureId} />}
          {isEpvalPage && <ConfirmAutofillDialog procedureId={procedureId} />}
          {isEpvalPage && <UnitaryEffectWarningDialog procedureId={procedureId} />}
          {isEPCTPage && (
            <EpctCreationDialog createEPCTDialog={createEPCTDialog} view={view} procedureId={procedureId} />
          )}
          {isEPPage && <EpCreationDialog createEPDialog={createEPDialog} view={view} />}

          <Dialog open={deleteDialog.isOpen} fullWidth maxWidth="lg">
            <DialogTitle>
              <DialogStatus variant="warning" />
              <Typography component="span" variant="h2">
                {t('messages.are-you-sure')}
              </Typography>
            </DialogTitle>
            <DialogContent>
              {t('submission.delete.confirmation', {
                amount: deleteDialog.values?.length,
              })}
              <Box p={isMediumOrBigger ? 4 : 2} className={classes.tableContainer}>
                <SubmissionTable submissions={deleteDialog.values} view={isMediumOrBigger ? view : DRAFT_VIEW.GRID} />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button id="cancelDelete" color="default" onClick={deleteDialog.close}>
                {t('actions.cancel')}
              </Button>
              <Button id="delete" color="primary" onClick={deleteDialog.confirm(deleteDialog.values)}>
                {t('actions.delete')}
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog open={changeStatusDialog.isOpen} fullWidth maxWidth="lg">
            <DialogTitle>
              <Typography component="h2" variant="h2" gutterBottom>
                {t('actions.status.change')}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Trans
                i18nKey="messages.submission.status.change"
                values={{
                  fromStatus:
                    fromStatus?.id === SubmissionStatus.READY_TO_SEND.id
                      ? t(SubmissionStatus.READY_TO_SEND.i18nKey)
                      : t(fromStatus?.i18nKey),
                  toStatus: t(toStatus?.i18nKey),
                }}
              />
            </DialogContent>
            <Box p={isMediumOrBigger ? 4 : 2} className={classes.tableContainer}>
              <SubmissionTable
                submissions={changeStatusDialog.values}
                view={isMediumOrBigger ? view : DRAFT_VIEW.GRID}
              />
            </Box>
            {changeStatusDialog.showCommentsSection && (
              <TextField
                autoFocus
                id="bulkChangeStatusComment"
                fullWidth
                multiline
                rows={4}
                value={changeStatusDialog.comments}
                onChange={event => changeStatusDialog.setComments(event.target.value)}
              />
            )}
            <DialogActions>
              <Button id="cancelDelete" color="default" onClick={changeStatusDialog.close}>
                {t('actions.cancel')}
              </Button>
              <Button id="delete" color="primary" onClick={changeStatusDialog.confirm(changeStatusDialog.values)}>
                {t('actions.confirm')}
              </Button>
            </DialogActions>
          </Dialog>

          <SendSubmissionDialog view={view} sendSubmissionDialog={sendSubmissionDialog} />

          <SentSubmissionProgressDialog
            sendSubmissionRequestProgress={sendSubmissionRequestProgress}
            sendSubmissionProcessDialog={sendSubmissionProcessDialog}
            sentSubmissions={sentSubmissions}
          />

          <TransferSubmissionDialog transferDialog={transferDialog} />

          {transferConfirmationDialog.isOpen && (
            <GenericConfirmationDialog
              dialog={transferConfirmationDialog}
              toggleDialog={transferConfirmationDialog.close}
              dialogVariant={transferConfirmationDialog.isError ? 'error' : 'success'}
              title={
                transferConfirmationDialog.isError
                  ? t('messages.submission.transfer.error.title')
                  : t('messages.submission.transfer.ok.title')
              }
              dialogMessage={
                transferConfirmationDialog.isError
                  ? t('messages.submission.transfer.error.description')
                  : t('messages.submission.transfer.ok.description')
              }
              buttonLabel={t('actions.ok')}
            />
          )}

          {selectedRow && (
            <SubmissionStatusChangeDialog
              open={revertStatusDialog.isOpen}
              handleCancel={revertStatusDialog.cancel}
              handleConfirm={revertStatusDialog.confirm}
              submission={selectedRow}
              revertStatusAction={revertStatusDialog.revertStatusAction}
            />
          )}
        </>
      )}
    </>
  );
}

export default DraftManagementDialogs;
